import { Box, SidePanel, Text } from '@wix/design-system';
import {
  LayoutSplitSmall,
  LayoutTwoColumnsSmall,
  StatusAlertFilledSmall,
} from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import type { FC } from 'react';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { EditorContext, ModalServiceContext, RicosContext } from './../../ricos-context';
import { useBiNext } from './../../_shared/bi-context-provider/bi-context-provider';
import { KEYS_CHARCODE } from './../../editor-common';
import { FooterActions, Panel, PanelHeader } from './../../ui-components';

import {
  INSERT_TABLE_MODAL,
  INSERT_TABLE_MODAL_COLUMNS_INPUT,
  INSERT_TABLE_MODAL_ERROR_MESSAGE,
  INSERT_TABLE_MODAL_FOOTER_ACTIONS,
  INSERT_TABLE_MODAL_ROWS_INPUT,
  ROOT,
} from '..//data-hooks';
import { getDefaultsSettings, isCellsNumberInvalid } from '..//tableUtil';
import TableSettingsCountSection from '..//toolbar/TableSettingsCountSection';
import { CELL_AUTO_MIN_WIDTH, ROW_DEFAULT_HEIGHT } from '../consts';
import styles from '../statics/styles/table-settings-modal-scss';
import { getDefaultTable } from '../tiptap/defaults';
import { INSERT_TABLE_MODAL_ID, TABLE_TYPE } from '../types';

export const InsertModal: FC = () => {
  const { t } = useContext(RicosContext);
  const modalService = useContext(ModalServiceContext) || {};
  const { getPublicCommands } = useContext(EditorContext);

  const [rowCount, setRowCount] = useState(4);
  const [colCount, setColCount] = useState(4);
  const [submittedInvalidCol, setSubmittedInvalidCol] = useState<boolean>();
  const [submittedInvalidRow, setSubmittedInvalidRow] = useState<boolean>();
  const [invalidCellNum, setInvalidCellNum] = useState<boolean>();
  const [isTyping, setIsTyping] = useState(false);

  const colsInput = useRef<HTMLInputElement | null>(null);
  const rowsInput = useRef<HTMLInputElement | null>(null);

  const logBi = useBiNext({ pluginId: 'table', modalId: INSERT_TABLE_MODAL_ID });

  useEffect(() => {
    colsInput?.current?.focus();
  }, []);

  useEffect(() => {
    if (isTyping) {
      const timer = setTimeout(() => {
        validateCounts();
        setIsTyping(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [colCount, rowCount, isTyping]);

  const validateCounts = () => {
    const invalidCol = colCount < 1;
    const invalidRow = rowCount < 1;
    const invalidCells = isCellsNumberInvalid(rowCount, colCount);

    setSubmittedInvalidCol(invalidCol);
    setSubmittedInvalidRow(invalidRow);
    setInvalidCellNum(invalidCells);

    return invalidCol || invalidRow || invalidCells;
  };

  const onCreateTableClicked = () => {
    const hasError = validateCounts();

    if (hasError) {
      if (submittedInvalidCol || invalidCellNum) {
        colsInput.current?.focus();
      } else {
        rowsInput.current?.focus();
      }
    } else {
      const { config } = getDefaultsSettings(rowCount, colCount);
      onTableAdd({ config });
    }
  };

  const onColCountChange = (colCount: number) => {
    setColCount(colCount);
    setIsTyping(true);
  };

  const onRowCountChange = (rowCount: number) => {
    setRowCount(rowCount);
    setIsTyping(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) =>
    e.keyCode === KEYS_CHARCODE.ENTER && onCreateTableClicked();

  const hasError = submittedInvalidCol || submittedInvalidRow || invalidCellNum;

  const closeModal = () => modalService.closeModal(INSERT_TABLE_MODAL_ID);

  const onTableAdd = (table: $TSFixMe) => {
    const rowNum = table.config.rowsHeight.length;
    const colNum = table.config.colsWidth.length;
    getPublicCommands().insertBlockWithBlankLines(TABLE_TYPE, {
      dimensions: {
        rowsHeight: Array(rowNum).fill(ROW_DEFAULT_HEIGHT),
        colsMinWidth: Array(colNum).fill(CELL_AUTO_MIN_WIDTH),
      },
      content: getDefaultTable(rowNum, colNum),
    });
    closeModal();
  };

  return (
    <Panel
      onCloseButtonClick={closeModal}
      dataHook={classNames(ROOT, INSERT_TABLE_MODAL)}
      height="100%"
    >
      <PanelHeader title={t('TablePlugin_SettingsModal_Title')} />

      <Box direction="vertical" padding="SP3" gap="SP3">
        <Box direction="vertical" gap="SP3">
          <Text size="small">{t('TablePlugin_SettingsModal_SubTitle')}</Text>
          <Box direction="horizontal" gap="SP3" className={styles.pickerField}>
            <TableSettingsCountSection
              title={t('TablePlugin_SettingsModal_ColCount')}
              input={colCount}
              onCountChange={onColCountChange}
              onBlur={validateCounts}
              dataHook={INSERT_TABLE_MODAL_COLUMNS_INPUT}
              hasError={submittedInvalidCol || invalidCellNum}
              inputRef={colsInput}
              icon={<LayoutTwoColumnsSmall />}
              onKeyDown={onKeyDown}
              logBi={logBi}
            />
            <TableSettingsCountSection
              title={t('TablePlugin_SettingsModal_RowCount')}
              input={rowCount}
              onCountChange={onRowCountChange}
              onBlur={validateCounts}
              dataHook={INSERT_TABLE_MODAL_ROWS_INPUT}
              hasError={submittedInvalidRow || invalidCellNum}
              inputRef={rowsInput}
              icon={<LayoutSplitSmall />}
              onKeyDown={onKeyDown}
              logBi={logBi}
            />
          </Box>
          {hasError && (
            <Box direction="horizontal" gap="9px" verticalAlign="middle">
              <Box color="R10">
                <StatusAlertFilledSmall />
              </Box>
              <Text size="small" skin="error" dataHook={INSERT_TABLE_MODAL_ERROR_MESSAGE}>
                {invalidCellNum
                  ? t('TablePlugin_SettingsModal_limitError')
                  : t('TablePlugin_SettingsModal_ErrorMessage')}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
      <SidePanel.Footer showDivider>
        <FooterActions
          onCancel={closeModal}
          onSave={() => {
            onCreateTableClicked();
          }}
          saveButtonLabel={t('TablePlugin_SettingsModal_CreateTable_Button')}
          logBi={logBi}
          dataHook={INSERT_TABLE_MODAL_FOOTER_ACTIONS}
        />
      </SidePanel.Footer>
    </Panel>
  );
};
